
import { supabase } from '@/integrations/supabase/client';
import { transformTourData } from '@/utils/tourTransformUtils';
import type { FlatTourData } from '@/utils/tourTransformUtils';
import type { DatabaseTour } from '@/types/supabase';

/**
 * Fetches published tours that should be publicly accessible
 * regardless of authentication status
 */
export async function fetchPublicTours(language: string = 'en'): Promise<FlatTourData[]> {
  try {
    console.log('[fetchPublicTours] Fetching published tours for public access');
    
    // Get environment info for better logging
    const hostname = typeof window !== 'undefined' ? window.location.hostname : 'server';
    const isProd = hostname !== 'localhost' && 
                  !hostname.includes('lovableproject.com');
                  
    console.log(`[fetchPublicTours] Environment: ${isProd ? 'PRODUCTION' : 'DEVELOPMENT/PREVIEW'}`);
    
    // Debug authentication status
    const { data: { user } } = await supabase.auth.getUser();
    console.log("[fetchPublicTours] Auth status:", user ? `Authenticated as ${user.email}` : 'Not authenticated (anonymous)');
    
    // Explicitly filter for published tours only
    const { data: toursData, error } = await supabase
      .from('tours')
      .select(`
        id,
        name,
        tour_type,
        duration,
        cover_image,
        status,
        created_at,
        updated_at,
        tags,
        display_order,
        maptype,
        mapimage,
        tour_translations (
          language_code,
          title,
          description
        )
      `)
      .eq('status', 'published') // Only fetch published tours for public access
      .order('display_order', { ascending: true, nullsFirst: false })
      .order('created_at', { ascending: true });
      
    if (error) {
      console.error('[fetchPublicTours] Error fetching tours:', error);
      throw error;
    }
    
    console.log(`[fetchPublicTours] Successfully fetched ${toursData?.length || 0} published tours`);
    
    // Transform each tour individually using the correct function signature
    const flattenedTours = (toursData || []).map(tour => 
      transformTourData(tour, language) // Use the provided language
    );
    
    // For each tour, fetch the exhibit count
    const toursWithExhibitCounts = await Promise.all(flattenedTours.map(async (tour) => {
      try {
        // Count the exhibits for this tour
        const { count, error: countError } = await supabase
          .from('tour_exhibits')
          .select('*', { count: 'exact', head: true })
          .eq('tour_id', tour.id);
          
        if (countError) {
          console.error('[fetchPublicTours] Error fetching exhibit count:', countError);
          return { ...tour, exhibitCount: 0 };
        }
        
        // Add the count to the tour object
        return {
          ...tour,
          exhibitCount: count || 0
        };
      } catch (err) {
        console.error('[fetchPublicTours] Error processing tour exhibit count:', err);
        return { ...tour, exhibitCount: 0 };
      }
    }));
    
    return toursWithExhibitCounts;
  } catch (error) {
    console.error('[fetchPublicTours] Error:', error);
    throw error;
  }
}
